import { useTranslation } from 'react-i18next'
import { useAppStore } from '../stores/appStore'
import { useState } from 'react'
import { serializeBedConfig } from '../utils/stateSerialization'
import { bedUrlToScreenshotUrl } from '../utils/screenshotUrl'
import { config } from '../config'
import * as THREE from 'three'

export default function Overview() {
  const { t } = useTranslation()
  const { bedConfig, setCurrentView, market, lang, bed, preferSeamless } = useAppStore()
  const [shareCopied, setShareCopied] = useState(false)
  const [screenshotUrlCopied, setScreenshotUrlCopied] = useState(false)
  
  const { bed: bedData, textile, legs, headboard, topper, bedsideTable, ottoman, accessories } = bedConfig
  
  const handleEditOption = (view: string) => {
    setCurrentView(view)
  }
  
  // Format legs value to show model and height if available
  const formatLegsValue = () => {
    if (!legs?.name) return null
    return legs.name
  }
  
  // Generate shareable URL with encoded state
  const generateShareUrl = () => {
    const params = new URLSearchParams()
    
    // Include market, lang, and bed as separate params for readability and compatibility
    params.set('market', market)
    params.set('lang', lang)
    if (bed) params.set('bed', bed)
    
    // Encode the entire bedConfig state for full restoration, including preferSeamless
    const encodedConfig = serializeBedConfig(bedConfig, market, lang, preferSeamless)
    params.set('config', encodedConfig)
    
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`
  }
  
  // Download canvas as PNG
  const handleDownloadPNG = () => {
    const gl = (window as any).__gl__
    const scene = (window as any).__scene__
    const camera = (window as any).__camera__
    if (!gl || !scene || !camera) {
      console.error('Renderer, scene, or camera not available')
      alert('Canvas not ready. Please wait a moment and try again.')
      return
    }
    
    // Store original state
    const originalBackground = scene.background
    const originalFog = scene.fog
    const clearColor = new THREE.Color()
    const originalClearColor = gl.getClearColor(clearColor)
    const originalClearAlpha = gl.getClearAlpha()
    
    // Store original renderer size and camera settings
    const originalSize = new THREE.Vector2()
    gl.getSize(originalSize)
    const originalAspect = camera.aspect
    const originalZoom = camera.zoom
    
    // Set background and fog to null for transparent capture
    scene.background = null
    scene.fog = null
    
    // Set clear color to transparent
    gl.setClearColor(0x000000, 0)
    
    // Render at higher resolution (4K)
    const targetWidth = 4096
    const targetHeight = 4096
    const liveAspect = originalSize.width / originalSize.height
    const outputAspect = targetWidth / targetHeight
    
    // Update camera for higher resolution
    camera.aspect = outputAspect
    if (liveAspect > outputAspect) {
      camera.zoom = originalZoom * (outputAspect / liveAspect)
    } else {
      camera.zoom = originalZoom
    }
    camera.updateProjectionMatrix()
    
    // Resize renderer to target resolution
    gl.setSize(targetWidth, targetHeight, false)
    gl.setViewport(0, 0, targetWidth, targetHeight)
    
    // Clear and render at high resolution
    gl.clear(true, true, true)
    gl.render(scene, camera)
    
    // Capture current canvas as PNG
    const dataURL = gl.domElement.toDataURL('image/png')
    
    const link = document.createElement('a')
    link.href = dataURL
    link.download = `bed-design-${bed || 'custom'}-${Date.now()}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Restore original state
    scene.background = originalBackground
    scene.fog = originalFog
    gl.setClearColor(originalClearColor, originalClearAlpha)
    camera.aspect = originalAspect
    camera.zoom = originalZoom
    camera.updateProjectionMatrix()
    gl.setSize(originalSize.width, originalSize.height, false)
    gl.setViewport(0, 0, originalSize.width, originalSize.height)
    
    // Force another render to restore the view
    gl.render(scene, camera)
  }
  
  // Copy shareable URL to clipboard
  const copyTextToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return true
      }
    } catch (err) {
      console.error('Failed to copy with clipboard API:', err)
    }

    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    try {
      const ok = document.execCommand('copy')
      document.body.removeChild(textArea)
      return ok
    } catch (err) {
      console.error('Failed to copy with execCommand:', err)
      document.body.removeChild(textArea)
      return false
    }
  }

  const handleShareUrl = async () => {
    const url = generateShareUrl()
    const copied = await copyTextToClipboard(url)
    if (copied) {
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    } else {
      alert(`Please copy this URL manually:\n${url}`)
    }
  }

  const handleCopyScreenshotUrl = async () => {
    const url = bedUrlToScreenshotUrl(generateShareUrl())
    const copied = await copyTextToClipboard(url)
    if (copied) {
      setScreenshotUrlCopied(true)
      setTimeout(() => setScreenshotUrlCopied(false), 2000)
    } else {
      alert(`Please copy this URL manually:\n${url}`)
    }
  }

  // Native share API (mobile/desktop)
  const handleNativeShare = async () => {
    const url = generateShareUrl()
    const shareData = {
      title: `${bedData.variation?.title || 'Bed Configuration'}`,
      text: `Check out my bed configuration: ${bedData.variation?.title || 'Custom bed'}`,
      url: url,
    }
    
    try {
      if (navigator.share) {
        await navigator.share(shareData)
      } else {
        // Fallback to copy URL
        handleShareUrl()
      }
    } catch (err) {
      // User cancelled or error - fallback to copy
      if ((err as Error).name !== 'AbortError') {
        handleShareUrl()
      }
    }
  }
  
  const SummaryItem = ({ 
    label, 
    value, 
    onClick
  }: { 
    label: string
    value: string | null
    onClick?: () => void
  }) => {
    if (!value) return null
    
    return (
      <div className="mb-4">
        {/* First line: Label and Change link */}
        <div className="flex justify-between items-center mb-1">
          <span className="text-gray-900 text-xs font-normal font-['Suisse_Int\'l']">
            {label}:
          </span>
          {onClick && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onClick()
              }}
              className="text-gray-900 text-base font-normal font-['Suisse_Int\'l'] underline cursor-pointer hover:opacity-70 transition-opacity"
            >
              {t('common.change')}
            </button>
          )}
        </div>
        {/* Second line: Value */}
        <div className="text-gray-900 text-base font-normal font-['Suisse_Int\'l']">
          {value}
        </div>
      </div>
    )
  }
  
  return (
    <div className="px-6 py-6 flex flex-col">
      {/* Bed model + size heading */}
      {(bedData.variation?.title || bedData.selectedSize) && (
        <div className="mb-6">
          <h1 className="text-gray-900 text-lg font-bold font-['Suisse_Int\'l']">
            {bedData.variation?.title}
            {bedData.variation?.title && bedData.selectedSize ? ', ' : ''}
            {bedData.selectedSize ? `${bedData.selectedSize} cm` : ''}
          </h1>
        </div>
      )}

      <div className="flex-shrink-0">
        {/* Textile */}
        <SummaryItem
          label={t('options.textile')}
          value={textile?.name || t('common.not-selected')}
          onClick={textile ? () => handleEditOption('textiles') : undefined}
        />
        
        {/* Topper */}
        <SummaryItem
          label={t('options.topper')}
          value={topper?.name || null}
          onClick={topper ? () => handleEditOption('toppers') : undefined}
        />
        
        {/* Legs */}
        <SummaryItem
          label={t('options.legs')}
          value={formatLegsValue()}
          onClick={legs ? () => handleEditOption('legs') : undefined}
        />
        
        {/* Headboard */}
        <SummaryItem
          label={t('options.headboard')}
          value={headboard?.name || null}
          onClick={headboard ? () => handleEditOption('headboards') : undefined}
        />
        
        {/* Bedside Table */}
        <SummaryItem
          label={t('options.bedside-table')}
          value={bedsideTable?.name || null}
          onClick={bedsideTable ? () => handleEditOption('bedside-tables') : undefined}
        />
        
        {/* Ottoman */}
        <SummaryItem
          label={t('options.ottoman')}
          value={ottoman?.name || null}
          onClick={ottoman ? () => handleEditOption('ottomans') : undefined}
        />
        
        {/* Accessories */}
        {accessories && accessories.length > 0 && (
          <SummaryItem
            label={t('options.accessories')}
            value={accessories.map(acc => acc.name).join(', ') || null}
            onClick={() => handleEditOption('accessories')}
          />
        )}
      </div>
      
      {/* Download and Share Buttons */}
      <div className="pt-6 mt-6 border-t border-gray-300 space-y-3">
        <button
          onClick={handleDownloadPNG}
          className="w-full py-3 px-4 transition-opacity hover:opacity-80 flex gap-2.5 items-center justify-center"
          style={{
            backgroundColor: '#696660',
            color: '#FFF',
            fontFamily: "Suisse Int'l",
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '14px',
            textTransform: 'uppercase'
          }}
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
    <path
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m3.5 6.5 4.5 5 4.5-5h-3v-6h-3v6z"
    ></path>
    <path
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.5 10.5V14a1.5 1.5 0 0 1-1.5 1.5H2A1.5 1.5 0 0 1 .5 14v-3.5"
    ></path>
  </svg>{t('common.download')}
        </button>
        
        <div className="flex gap-3">
          <button
            onClick={handleShareUrl}
            className="flex-1 py-3 px-4 transition-opacity hover:opacity-80 flex gap-2.5 items-center justify-center"
            style={{
              backgroundColor: '#696660',
              color: '#FFF',
              fontFamily: "Suisse Int'l",
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 300,
              lineHeight: '14px',
              textTransform: 'uppercase'
            }}
          >
            {shareCopied ? t('common.copied') : t('common.copy-link')}
          </button>
          
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <button
              onClick={handleNativeShare}
              className="flex-1 py-3 px-4 transition-opacity hover:opacity-80 flex gap-2.5 items-center justify-center"
              style={{
                backgroundColor: '#696660',
                color: '#FFF',
                fontFamily: "Suisse Int'l",
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 300,
                lineHeight: '14px',
                textTransform: 'uppercase'
              }}
            >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
    <g
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      clipPath="url(#a)"
    >
      <path d="M5.5 3.5H2A1.5 1.5 0 0 0 .5 5v9A1.5 1.5 0 0 0 2 15.5h10a1.5 1.5 0 0 0 1.5-1.5v-3.5m-2-10 4 4-4 4"></path>
      <path d="M15.5 4.5h-5a4 4 0 0 0-4 4v2"></path>
    </g>
    <defs>
      <clipPath id="a">
        <path fill="#fff" d="M0 0h16v16H0z"></path>
      </clipPath>
    </defs>
  </svg>
              {t('common.share')}
            </button>
          )}
        </div>

        {config.isDevelopment && (
          <button
            type="button"
            onClick={handleCopyScreenshotUrl}
            className="w-full py-2 px-4 transition-opacity hover:opacity-80 text-left"
            style={{
              border: '1px dashed #696660',
              color: '#696660',
              fontFamily: "Suisse Int'l",
              fontSize: '13px',
              fontWeight: 300,
              lineHeight: '18px',
            }}
          >
            {screenshotUrlCopied ? 'Screenshot URL copied' : 'Copy screenshot URL (dev)'}
          </button>
        )}
      </div>
    </div>
  )
}
